import React, { useState, useEffect, useRef } from 'react';
import { MapPin, ShieldCheck, Clock, Headphones, DollarSign, MousePointerClick } from 'lucide-react';
import { features } from '../mockData';

const iconMap = {
  'map-pin': MapPin,
  'shield-check': ShieldCheck,
  'clock': Clock,
  'headphones': Headphones,
  'dollar-sign': DollarSign,
  'mouse-pointer-click': MousePointerClick
};

// Custom animated counter component
const AnimatedCounter = ({ end, suffix = '', duration = 2500 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef(null);
  const animationRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      setCount(end);
      return;
    }

    const currentCounter = counterRef.current;

    // IntersectionObserver to detect when counter becomes visible
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          } else {
            // Reset when out of view to allow replay
            setIsVisible(false);
            setCount(0);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (currentCounter) {
      observerRef.current.observe(currentCounter);
    }

    return () => {
      if (observerRef.current && currentCounter) {
        observerRef.current.unobserve(currentCounter);
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [end]);

  useEffect(() => {
    if (!isVisible) return;

    const startTime = Date.now();
    const startValue = 0;

    const animate = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out cubic function
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      const currentCount = Math.floor(startValue + (end - startValue) * easeOut);
      setCount(currentCount);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isVisible, end, duration]);

  return (
    <span ref={counterRef}>
      {count}{suffix}
    </span>
  );
};

const FeaturesSection = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 via-red-50 to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16" data-aos="fade-up">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Shippzy?</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            We provide comprehensive logistics solutions with customer satisfaction as our top priority
          </p>
        </div>

        {/* Stats Row with Animated Counters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-aos="fade-up">
          <div className="text-center p-6 bg-white rounded-xl shadow-md">
            <div className="text-5xl font-bold text-red-600 mb-2">
              <AnimatedCounter end={185} suffix="+" duration={2500} />
            </div>
            <div className="text-gray-600 font-medium">Countries Served</div>
          </div>
          <div className="text-center p-6 bg-white rounded-xl shadow-md">
            <div className="text-5xl font-bold text-red-600 mb-2">
              <AnimatedCounter end={75} suffix="%" duration={2500} />
            </div>
            <div className="text-gray-600 font-medium">Cost Savings</div>
          </div>
          <div className="text-center p-6 bg-white rounded-xl shadow-md">
            <div className="text-5xl font-bold text-red-600 mb-2">24/7</div>
            <div className="text-gray-600 font-medium">Customer Support</div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const IconComponent = iconMap[feature.icon];
            return (
              <div
                key={feature.id}
                className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors duration-300">
                  <IconComponent className="w-7 h-7 text-red-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-red-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
